import { useMemo } from 'react';
import { useNotifications } from '@/providers/notificationHooks';
import { getMessage, type UiMessageArgs, type UiMessageKey } from './messages';
import type { UiTone } from '@/theme/uiTokens';

type NotificationOptions<K extends UiMessageKey> = {
  tone?: UiTone;
  timeoutMs?: number;
  args?: UiMessageArgs<K>;
};

const createNotifyCopy = (
  notify: ReturnType<typeof useNotifications>['notify'],
): (<K extends UiMessageKey>(key: K, options?: NotificationOptions<K>) => void) => {
  return (key, options) => {
    notify({
      message: getMessage(key, options?.args),
      tone: options?.tone,
      timeoutMs: options?.timeoutMs,
    });
  };
};

export const useCopyNotifications = (): {
  notifyCopy: <K extends UiMessageKey>(key: K, options?: NotificationOptions<K>) => void;
  notifySuccess: <K extends UiMessageKey>(key: K, args?: UiMessageArgs<K>) => void;
  notifyError: <K extends UiMessageKey>(key: K, args?: UiMessageArgs<K>) => void;
  notifyInfo: <K extends UiMessageKey>(key: K, args?: UiMessageArgs<K>) => void;
} => {
  const { notify } = useNotifications();

  return useMemo(() => {
    const notifyCopy = createNotifyCopy(notify);
    return {
      notifyCopy,
      notifySuccess: (key, args) => {
        notifyCopy(key, { args, tone: 'green' });
      },
      notifyError: (key, args) => {
        notifyCopy(key, { args, tone: 'red' });
      },
      notifyInfo: (key, args) => {
        notifyCopy(key, { args, tone: 'blue' });
      },
    };
  }, [notify]);
};
